'use client';

import { Button, ButtonType } from '@/components/ui/button';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslations } from 'next-intl';
import { MdChevronRight } from 'react-icons/md';

import type { SiteNavNodeView } from '@/components/site-page/SitePage.types';
import { SiteHeaderNavLink, SiteHeaderNodeContent } from './SiteHeaderParts';
import {
  dropdownClasses,
  dropdownDividerClasses,
  dropdownItemClasses,
  flyoutClasses,
  flyoutHostClasses,
} from './SiteHeader.style';
import { currentMark, isCurrentLink, useNavLocation } from './site-header-utils';

/** The focusable items of ONE list (a flyout's items are not part of the panel's list). */
function listItems(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>('[data-nav-item]')).filter(
    (item) => item.closest('[data-nav-list]') === container
  );
}

/** ↑/↓/Home/End between the items of `container`. Returns true when the key was handled. */
function moveFocus(event: KeyboardEvent<HTMLElement>, container: HTMLElement): boolean {
  const items = listItems(container);
  if (items.length === 0) return false;
  const index = items.indexOf(document.activeElement as HTMLElement);
  let next: number;
  switch (event.key) {
    case 'ArrowDown':
      next = index < 0 ? 0 : (index + 1) % items.length;
      break;
    case 'ArrowUp':
      next = index <= 0 ? items.length - 1 : index - 1;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = items.length - 1;
      break;
    default:
      return false;
  }
  event.preventDefault();
  items[next].focus();
  return true;
}

interface SiteHeaderDropdownProps {
  node: SiteNavNodeView;
  panelId: string;
  /** Opened from the keyboard: focus the first item on mount. */
  focusFirst: boolean;
  /** `returnFocus` true puts focus back on the group's toggle. */
  onClose: (returnFocus: boolean) => void;
}

/**
 * The panel of a top-level group: Overview first (when resolved), then its items, with a level-3
 * flyout for a nested group.
 *
 * ## Invariants:
 * - Escape closes the INNERMOST open level and returns focus to its trigger: a flyout first, then
 *   the panel (focus to the group toggle).
 * - Arrow keys move within one list; → or Enter on a nested group opens its flyout, ← closes it.
 * - Item and overview text is rendered as text; hrefs come from the mapped, validated links.
 */
export function SiteHeaderDropdown({
  node,
  panelId,
  focusFirst,
  onClose,
}: SiteHeaderDropdownProps) {
  const t = useTranslations('Navigation');
  const location = useNavLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [flyoutId, setFlyoutId] = useState<string | null>(null);

  useEffect(() => {
    if (focusFirst && panelRef.current) listItems(panelRef.current)[0]?.focus();
  }, [focusFirst]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const panel = event.currentTarget;
    if ((event.target as HTMLElement).closest('[data-nav-list]') !== panel) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose(true);
      return;
    }
    moveFocus(event, panel);
  };

  return (
    <div
      ref={panelRef}
      id={panelId}
      className={dropdownClasses}
      data-cy="site-header-dropdown"
      data-node-id={node.id}
      data-nav-list=""
      onKeyDown={handleKeyDown}
    >
      {node.overview && (
        <>
          <SiteHeaderNavLink
            link={node.overview}
            className={dropdownItemClasses}
            data-cy="site-header-dropdown-overview"
            aria-current={isCurrentLink(node.overview, location) ? 'page' : undefined}
            onClick={() => onClose(false)}
          >
            {t('overview')}
          </SiteHeaderNavLink>
          <hr className={dropdownDividerClasses} />
        </>
      )}
      {node.children.map((child) =>
        child.kind === 'GROUP' ? (
          <Flyout
            key={child.id}
            node={child}
            open={flyoutId === child.id}
            onOpen={() => setFlyoutId(child.id)}
            onClose={() => setFlyoutId(null)}
            onCloseAll={() => onClose(false)}
          />
        ) : (
          <PanelItem key={child.id} node={child} onDone={() => onClose(false)} />
        )
      )}
    </div>
  );
}

function PanelItem({ node, onDone }: { node: SiteNavNodeView; onDone: () => void }) {
  const location = useNavLocation();
  const content = <SiteHeaderNodeContent node={node} showLabel iconCy="site-header-node-icon" />;
  if (node.link) {
    return (
      <SiteHeaderNavLink
        link={node.link}
        className={dropdownItemClasses}
        data-cy="site-header-dropdown-item"
        data-node-kind={node.kind}
        aria-current={currentMark(node, location) === 'page' ? 'page' : undefined}
        onClick={onDone}
      >
        {content}
      </SiteHeaderNavLink>
    );
  }
  return (
    <Button
      buttonType={ButtonType.text}
      className={dropdownItemClasses}
      data-cy="site-header-dropdown-item"
      data-node-kind={node.kind}
      data-nav-item=""
      onClick={() => {
        node.onActivate?.();
        onDone();
      }}
    >
      {content}
    </Button>
  );
}

interface FlyoutProps {
  node: SiteNavNodeView;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCloseAll: () => void;
}

/** A level-2 group: a trigger inside the panel and its level-3 items beside it. */
function Flyout({ node, open, onOpen, onClose, onCloseAll }: FlyoutProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const focusFirstRef = useRef(false);

  useEffect(() => {
    if (open && focusFirstRef.current && flyoutRef.current) {
      listItems(flyoutRef.current)[0]?.focus();
    }
    focusFirstRef.current = false;
  }, [open]);

  const closeToTrigger = () => {
    onClose();
    triggerRef.current?.focus();
  };

  return (
    <div className={flyoutHostClasses}>
      <Button
        buttonType={ButtonType.text}
        ref={triggerRef}
        className={dropdownItemClasses}
        aria-expanded={open}
        aria-haspopup="true"
        data-cy="site-header-dropdown-item"
        data-node-kind={node.kind}
        data-nav-item=""
        onClick={(event) => {
          if (open) onClose();
          else {
            focusFirstRef.current = event.detail === 0;
            onOpen();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            focusFirstRef.current = true;
            onOpen();
          } else if (event.key === 'ArrowLeft' && open) {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <span className="tw:flex tw:items-center tw:gap-small">
          <SiteHeaderNodeContent node={node} showLabel iconCy="site-header-node-icon" />
        </span>
        <MdChevronRight size={20} aria-hidden="true" />
      </Button>
      {open && (
        <div
          ref={flyoutRef}
          className={flyoutClasses}
          data-cy="site-header-flyout"
          data-nav-list=""
          onKeyDown={(event) => {
            if (event.key === 'Escape' || event.key === 'ArrowLeft') {
              event.preventDefault();
              event.stopPropagation();
              closeToTrigger();
              return;
            }
            if (moveFocus(event, event.currentTarget)) event.stopPropagation();
          }}
        >
          {node.children.map((child) => (
            <PanelItem key={child.id} node={child} onDone={onCloseAll} />
          ))}
        </div>
      )}
    </div>
  );
}
